import { useEffect, useRef, useCallback, useLayoutEffect, useState, useMemo } from 'react';
import { MessageItem } from './MessageItem';
import { TypingBubble } from './TypingBubble';

function messageId(message) {
    return String(message.clientId || message._id);
}

const PIN_DELAYS_MS = [0, 50, 150, 350, 600];
const REVEAL_STAGGER_MS = 45;
const REVEAL_STAGGER_MAX_MS = 225;

export const MessageList = ({
    messages = [],
    conversationId,
    currentUserId,
    onReply,
    onRetry,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
    participants = [],
    typingUsers = [],
    localComposing = false,
}) => {
    const containerRef = useRef(null);
    const prevLengthRef = useRef(0);
    const prevScrollHeightRef = useRef(0);
    const prevScrollTopRef = useRef(0);
    const isPrependingRef = useRef(false);
    const loadingRef = useRef(false);
    const knownIdsRef = useRef(new Set());
    const observerRef = useRef(null);
    const pinToBottomRef = useRef(true);
    const pinTimersRef = useRef([]);
    const [enterIds, setEnterIds] = useState(() => new Set());

    const clearPinTimers = useCallback(() => {
        pinTimersRef.current.forEach((id) => window.clearTimeout(id));
        pinTimersRef.current = [];
    }, []);

    const scrollToBottom = useCallback((smooth = false, force = false) => {
        const container = containerRef.current;
        if (!container) return;
        if (!force && !pinToBottomRef.current) return;

        const apply = () => {
            const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
            if (smooth && container.scrollTo) {
                container.scrollTo({ top: maxScroll, behavior: 'smooth' });
            } else {
                container.scrollTop = maxScroll;
            }
        };

        apply();
        requestAnimationFrame(() => {
            apply();
            requestAnimationFrame(apply);
        });
    }, []);

    const schedulePinToBottom = useCallback((smooth = false) => {
        clearPinTimers();
        pinToBottomRef.current = true;
        scrollToBottom(smooth, true);
        PIN_DELAYS_MS.forEach((ms) => {
            const id = window.setTimeout(() => scrollToBottom(false, true), ms);
            pinTimersRef.current.push(id);
        });
    }, [clearPinTimers, scrollToBottom]);

    useEffect(() => {
        prevLengthRef.current = 0;
        knownIdsRef.current = new Set();
        pinToBottomRef.current = true;
        clearPinTimers();
    }, [conversationId, clearPinTimers]);

    useEffect(() => () => clearPinTimers(), [clearPinTimers]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        /*
         * Los mensajes antiguos aparecen al subir: entran desde arriba y escalonados
         * del más cercano al contenido actual hacia el más lejano.
         */
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((entry) => entry.isIntersecting);
                if (!visible.length) return;

                const rootBounds = visible[0].rootBounds || container.getBoundingClientRect();
                const midline = rootBounds.top + rootBounds.height / 2;

                visible
                    .sort((a, b) => b.boundingClientRect.top - a.boundingClientRect.top)
                    .forEach((entry, index) => {
                        const el = entry.target;
                        const delay = Math.min(index * REVEAL_STAGGER_MS, REVEAL_STAGGER_MAX_MS);
                        el.style.setProperty('--chat-stagger', `${delay}ms`);
                        if (entry.boundingClientRect.bottom < midline) {
                            el.classList.add('chat-message--from-above');
                        }
                        el.classList.add('chat-message--in-view');
                        observer.unobserve(el);
                    });
            },
            {
                root: container,
                threshold: 0.08,
                rootMargin: '0px 0px -12px 0px',
            },
        );

        observerRef.current = observer;
        container.querySelectorAll('.chat-message:not(.chat-message--in-view)').forEach((el) => {
            if (!el.classList.contains('chat-message--enter')) {
                observer.observe(el);
            }
        });

        return () => observer.disconnect();
    }, [messages]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const prevLen = prevLengthRef.current;
        const newLen = messages.length;
        const isInitialLoad = prevLen === 0 && newLen > 0;
        const addedIds = [];

        messages.forEach((message) => {
            const id = messageId(message);
            if (!knownIdsRef.current.has(id)) {
                if (knownIdsRef.current.size > 0 && !isPrependingRef.current) {
                    addedIds.push(id);
                }
                knownIdsRef.current.add(id);
            }
        });

        if (isInitialLoad) {
            container.querySelectorAll('.chat-message').forEach((el, index) => {
                el.style.setProperty('--chat-stagger', `${Math.min(index * 36, 280)}ms`);
                el.classList.add('chat-message--in-view');
            });
            schedulePinToBottom(false);
        } else if (newLen > prevLen) {
            if (isPrependingRef.current) {
                const diff = container.scrollHeight - prevScrollHeightRef.current;
                container.scrollTop = prevScrollTopRef.current + diff;
                isPrependingRef.current = false;
            } else if (addedIds.length > 0) {
                pinToBottomRef.current = true;
                if (addedIds.length === 1) {
                    scrollToBottom(true, true);
                } else {
                    schedulePinToBottom(false);
                }
                setEnterIds(new Set(addedIds));
                const clearEnterId = window.setTimeout(() => setEnterIds(new Set()), 520);
                pinTimersRef.current.push(clearEnterId);
            }
        }

        prevLengthRef.current = newLen;
    }, [messages, scrollToBottom, schedulePinToBottom]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        const ro = new ResizeObserver(() => {
            if (pinToBottomRef.current) {
                scrollToBottom(false, true);
            }
        });

        ro.observe(container);
        return () => ro.disconnect();
    }, [scrollToBottom, conversationId]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        // `load` no burbujea: se escucha en captura para imágenes y videos del historial.
        const onMediaLoad = () => {
            if (pinToBottomRef.current) scrollToBottom(false, true);
        };

        container.addEventListener('load', onMediaLoad, true);
        return () => container.removeEventListener('load', onMediaLoad, true);
    }, [scrollToBottom]);

    const loadOlder = useCallback(async () => {
        if (!hasMore || loadingRef.current || loadingMore || !onLoadMore) return;

        const container = containerRef.current;
        if (container) {
            prevScrollHeightRef.current = container.scrollHeight;
            prevScrollTopRef.current = container.scrollTop;
            isPrependingRef.current = true;
            pinToBottomRef.current = false;
        }

        loadingRef.current = true;
        try {
            await onLoadMore();
        } finally {
            loadingRef.current = false;
        }
    }, [hasMore, loadingMore, onLoadMore]);

    const handleScroll = () => {
        const container = containerRef.current;
        if (!container) return;

        const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        pinToBottomRef.current = distFromBottom < 48;

        if (!hasMore || loadingRef.current || loadingMore) return;
        if (container.scrollTop < 80) {
            void loadOlder();
        }
    };

    const registerMessageRef = useCallback((id, el) => {
        if (!el || !observerRef.current) return;
        if (el.classList.contains('chat-message--in-view') || el.classList.contains('chat-message--enter')) {
            return;
        }
        observerRef.current.observe(el);
    }, []);

    const activeTypingUsers = useMemo(
        () => typingUsers.filter(
            (entry) => (
                String(entry.conversationId) === String(conversationId)
                && String(entry.user?._id) !== String(currentUserId)
            ),
        ),
        [typingUsers, conversationId, currentUserId],
    );

    const showTypingBubbles = localComposing || activeTypingUsers.length > 0;

    useEffect(() => {
        if (!showTypingBubbles) return;
        schedulePinToBottom(true);
    }, [showTypingBubbles, localComposing, activeTypingUsers.length, schedulePinToBottom]);

    return (
        <div
            ref={containerRef}
            onScroll={handleScroll}
            className="chat-messages"
        >
            {loadingMore && (
                <div className="chat-messages__loading">
                    Cargando mensajes anteriores…
                </div>
            )}

            {!loadingMore && hasMore && (
                <div className="chat-messages__load">
                    <button
                        type="button"
                        onClick={() => void loadOlder()}
                        className="chat-messages__load-btn"
                    >
                        Cargar mensajes anteriores
                    </button>
                </div>
            )}

            {messages.length === 0 && !loadingMore && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <p className="text-sm font-semibold text-slate-600">Sin mensajes aún</p>
                    <p className="text-xs text-slate-400 mt-1">Escribe el primero para iniciar la conversación.</p>
                </div>
            )}

            {messages.map((message) => {
                const id = messageId(message);
                return (
                    <MessageItem
                        key={id}
                        ref={(el) => registerMessageRef(id, el)}
                        message={message}
                        isOwn={String(message.sender?._id) === String(currentUserId)}
                        currentUserId={currentUserId}
                        onReply={onReply}
                        onRetry={onRetry}
                        participants={participants}
                        entering={enterIds.has(id)}
                    />
                );
            })}

            {activeTypingUsers.map((entry) => {
                const typingUserId = entry.user?._id?.toString?.() || entry.user?._id;
                const senderName = [entry.user?.firstName, entry.user?.lastName].filter(Boolean).join(' ');
                return (
                    <TypingBubble
                        key={`typing-${typingUserId}`}
                        senderName={senderName}
                    />
                );
            })}

            {localComposing ? (
                <TypingBubble isOwn />
            ) : null}
        </div>
    );
};
